import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type {
	AutomaticSourcesMode,
	PreviousStepDocumentsMode,
} from '@Application/Organizations/Workflows/Actions/organizationWorkflowsFetched';
import { theme } from '@Guidelines/Theme/theme';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import CheckboxTile from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/CheckboxTile';
import HintText from '@Guidelines/Component/HintText';
import PillChoice from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/PillChoice';
import SectionCard from '@Guidelines/Component/SectionCard';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';

const PREVIOUS_MODES = [
	{ label: 'Tous', value: 'all' },
	{ label: 'Au choix', value: 'selected' },
	{ label: 'Aucun', value: 'none' },
] as const;

const AUTOMATIC_MODES = [
	{ label: 'Tous', value: 'all' },
	{ label: 'Au choix', value: 'selected' },
] as const;

const blockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const blockTitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.4;
	color: ${theme.colors.ink};
`;

type Props = {
	readonly settings: DocumentSettingsFormData;
	readonly stageIndex: number;
	readonly onChange: React.Dispatch<React.SetStateAction<DocumentSettingsFormData>>;
};

/**
 * What the document receives: the preparation's elements, the documents of the stages before its own
 * (absent from the first stage) and the documents the group takes over from the file. The candidates
 * are read from the group draft, so only what the group really provides can be picked.
 */
const InputsSection: React.FC<Props> = ({ settings, stageIndex, onChange }) => {
	const { draft } = useWorkflowDraft();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);

	const previousCandidates = React.useMemo(
		() =>
			draft.previousDocumentTypeIds(stageIndex).map((documentTypeId) => ({
				value: documentTypeId,
				label: documentTypes?.labelOf(documentTypeId) ?? 'Type inconnu',
			})),
		[draft, stageIndex, documentTypes],
	);

	const automaticCandidates = React.useMemo(
		() =>
			draft.automaticSources.map((source) => {
				const typeLabel = (documentTypes?.labelOf(source.documentTypeId) ?? 'document').toLowerCase();

				return {
					value: source.documentTypeId,
					label: `Le dernier ${typeLabel}${source.verifiedOnly ? ' vérifié' : ''}`,
				};
			}),
		[draft.automaticSources, documentTypes],
	);

	const handlePreparationElementsChange = React.useCallback(
		(checked: boolean) => {
			onChange((current) => current.setPreparationElements(checked));
		},
		[onChange],
	);

	const handlePreviousModeChange = React.useCallback(
		(value: PreviousStepDocumentsMode) => {
			onChange((current) => current.setPreviousStepDocumentsMode(value));
		},
		[onChange],
	);

	const handleAutomaticModeChange = React.useCallback(
		(value: AutomaticSourcesMode) => {
			onChange((current) => current.setAutomaticSourcesMode(value));
		},
		[onChange],
	);

	const handlePreviousPickChange = React.useCallback(
		(value: readonly number[]) => {
			onChange((current) => current.setPreviousStepDocumentTypeIds(value));
		},
		[onChange],
	);

	const handleAutomaticPickChange = React.useCallback(
		(value: readonly number[]) => {
			onChange((current) => current.setAutomaticSourceDocumentTypeIds(value));
		},
		[onChange],
	);

	return (
		<SectionCard title="Ce que ce document reçoit">
			<CheckboxTile
				name="preparationElements"
				label="Les éléments de la préparation"
				checked={settings.preparationElements}
				onChange={handlePreparationElementsChange}
			/>
			<div css={blockStyle}>
				<p css={blockTitleStyle}>Les documents des étapes précédentes</p>
				{stageIndex === 0 ? (
					<HintText>Première étape : il n’y a pas d’étape précédente.</HintText>
				) : (
					<React.Fragment>
						<PillChoice
							name="previousStepDocumentsMode"
							label="Documents des étapes précédentes"
							isLabelHidden
							options={PREVIOUS_MODES}
							value={settings.previousStepDocumentsMode}
							onChange={handlePreviousModeChange}
						/>
						<ConditionalLoader data={settings.previousStepDocumentsMode === 'selected'}>
							<PillChoice
								multiple
								name="previousStepDocumentTypeIds"
								label="Lesquels"
								options={previousCandidates}
								value={settings.previousStepDocumentTypeIds}
								onChange={handlePreviousPickChange}
							/>
						</ConditionalLoader>
					</React.Fragment>
				)}
			</div>
			<div css={blockStyle}>
				<p css={blockTitleStyle}>Les documents repris automatiquement</p>
				{automaticCandidates.length === 0 ? (
					<HintText>Ce groupe ne reprend aucun document.</HintText>
				) : (
					<React.Fragment>
						<PillChoice
							name="automaticSourcesMode"
							label="Documents repris"
							isLabelHidden
							options={AUTOMATIC_MODES}
							value={settings.automaticSourcesMode}
							onChange={handleAutomaticModeChange}
						/>
						<ConditionalLoader data={settings.automaticSourcesMode === 'selected'}>
							<PillChoice
								multiple
								name="automaticSourceDocumentTypeIds"
								label="Lesquels"
								options={automaticCandidates}
								value={settings.automaticSourceDocumentTypeIds}
								onChange={handleAutomaticPickChange}
							/>
						</ConditionalLoader>
					</React.Fragment>
				)}
			</div>
		</SectionCard>
	);
};

export default InputsSection;
